import {
  Building2,
  CalendarDays,
  CalendarPlus,
  DatabaseZap,
  type LucideIcon,
  ReceiptText,
  ScanLine,
  Shield,
  ShieldCheck,
  Sparkles,
  Stethoscope,
  UserRound,
  Users,
} from 'lucide-react';

import { P } from '@/features/permissions';
import type { Edition } from '@/lib/auth/AuthContext';

export interface NavGroup {
  items: NavItem[];
  label: string;
  /** When set, hide this entire group unless the current edition matches. */
  requiredEdition?: Edition;
}

export interface NavItem {
  badge?: number;
  external?: boolean;
  href: string;
  icon: LucideIcon;
  isActive?: (pathname: string) => boolean;
  /** When set, hide this item unless the current edition matches. */
  requiredEdition?: Edition;
  requiredPermission?: string;
  requiredPermissions?: string[];
  /** When true, hide this item from everyone except system superusers. */
  requireSuperuser?: boolean;
  /**
   * When true, show only where the ConsultNow importer is configured and the
   * user administers the organization it writes into. Answered by the server,
   * because neither fact is in the token.
   */
  requireImportAccess?: boolean;
  title: string;
}

/**
 * Quick Start, promoted out of the nav list.
 *
 * It is the app's primary action — start recording — and it was reading as the
 * fifth item in a list of five. `AppSidebar` renders it as a CTA in the sidebar
 * header instead, so it deliberately does NOT appear in `mainNavigation` below.
 *
 * Wrapped in a group so it can go through `useFilteredNavigation` like every
 * other item: the edition and permission rules stay in one place rather than
 * being re-implemented at the call site, where they would drift.
 *
 * Module-level constant on purpose — `useFilteredNavigation` memoises on the
 * array's identity, so a literal built during render would recompute forever.
 */
export const quickStartNav: NavItem = {
  icon: Sparkles,
  href: '/quick-start',
  title: 'Quick Start',
  requiredEdition: 'professional',
  isActive: (pathname) => pathname.startsWith('/quick-start'),
};

export const quickStartNavGroup: NavGroup[] = [{ label: 'Quick Start', items: [quickStartNav] }];

// NOTE: Items here can be hidden in the personal-edition UI by setting
// `requiredEdition: 'professional'`. Plan 5 marks every item that should not
// appear in personal mode. Add `requiredEdition` to new items as appropriate.
export const mainNavigation: NavGroup[] = [
  {
    label: 'Clinical',
    items: [
      {
        title: 'Inbox',
        icon: Stethoscope,
        href: '/encounters',
        requiredEdition: 'professional',
        requiredPermissions: [P.READ_ENCOUNTER, P.READ_DOCUMENT],
        isActive: (pathname) => pathname.startsWith('/encounters') || pathname.startsWith('/documents'),
      },
      {
        icon: ScanLine,
        href: '/imaging',
        title: 'Imaging',
        requiredEdition: 'professional',
        requiredPermissions: ['intake_imaging', 'read_imaging', 'prepare_imaging'],
        isActive: (path) => path.startsWith('/imaging'),
      },
      {
        href: '/calendar',
        title: 'Calendar',
        icon: CalendarDays,
        requiredEdition: 'professional',
        requiredPermission: P.READ_ENCOUNTER,
        isActive: (pathname) => pathname.startsWith('/calendar'),
      },
      {
        icon: UserRound,
        href: '/patients',
        title: 'Patients',
        requiredEdition: 'professional',
        requiredPermission: P.READ_PATIENT,
        isActive: (pathname) => pathname.startsWith('/patients'),
      },

    ],
  },
  {
    label: 'AI Tools',
    requiredEdition: 'professional',
    items: [
      {
        icon: CalendarPlus,
        href: '/upload-schedule',
        title: 'Upload Schedule',
        requiredEdition: 'professional',
        requiredPermission: P.CREATE_ENCOUNTER,
        isActive: (pathname) => pathname.startsWith('/upload-schedule'),
      },
    ],
  },
  {
    // Billing, not Clinical. The handoff put OON claims above Encounters, which
    // reads as though filing claims outranks seeing patients. It is also the
    // only home for Eligibility, which shipped as a route with no way to reach
    // it but by typing the URL.
    label: 'Billing',
    requiredEdition: 'professional',
    items: [
      {
        href: '/claims',
        icon: ReceiptText,
        title: 'OON claims',
        requiredEdition: 'professional',
        requiredPermission: P.READ_CLAIM,
        isActive: (pathname) => pathname.startsWith('/claims'),
      },
      {
        icon: ShieldCheck,
        href: '/eligibility',
        title: 'Eligibility',
        requiredEdition: 'professional',
        requiredPermission: P.READ_CLAIM,
        isActive: (pathname) => pathname.startsWith('/eligibility'),
      },
    ],
  },
  {
    label: 'Administration',
    items: [
      {
        icon: Building2,
        href: '/organization',
        title: 'Organization',
        requiredEdition: 'professional',
        requiredPermission: P.MANAGE_ORGANIZATION,
        isActive: (pathname) => pathname.startsWith('/organization'),
      },
      {
        icon: Users,
        href: '/users',
        title: 'Users',
        requiredPermission: P.MANAGE_USERS,
        isActive: (pathname) => pathname.startsWith('/users'),
      },
      {
        icon: Shield,
        href: '/roles',
        title: 'Roles',
        requiredEdition: 'professional',
        requiredPermission: P.MANAGE_USERS,
        isActive: (pathname) => pathname.startsWith('/roles'),
      },
      // "Import from ConsultNow" was here. The ConsultNow V2 migration finished
      // on 2026-10-05, so the menu entry is gone, but everything behind it is
      // deliberately kept: the importer is a working pattern for pulling
      // patients out of another system and is expected to be reused.
      //
      // Still live, just not advertised: the /import route, the v2_import
      // feature slice, `requireImportAccess`, and the V2_* settings. The page
      // is reachable by URL and still refuses anyone who is not an
      // administrator of the destination organization. To bring the item back,
      // restore this entry with `icon: DatabaseZap, href: '/import',
      // requireImportAccess: true` and retitle it for whatever system it
      // imports from next.
    ],
  },
];
